import React, { useEffect, useState } from 'react';
import { 
  Title, 
  ObjectStatus, 
  FlexBox, 
  FlexBoxJustifyContent, 
  FlexBoxDirection,
  FlexBoxAlignItems,
  AnalyticalTable,
  Text,
  Button,
  Icon,
  Label,
  MessageStrip,
  BusyIndicator,
  Dialog,
  Select,
  Option
} from '@ui5/webcomponents-react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import Navbar from '../Navbar';
import SideMenu from '../SideMenu';
import { parseToNumber, formatNumberWithComma, rightAlignedNumberStyle, getStatusStyle, getStatusComponentProps } from '../../utils/validator';
import usePermission from '../../hooks/usePermission';
import useAutoTranslate from '../../hooks/useAutoTranslate';

const RampDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rampData, setRampData] = useState(null);
  const [assignmentItemDetails, setAssignmentItemDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignmentItemsLoading, setAssignmentItemsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assignmentItemsError, setAssignmentItemsError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [asnDetails, setAsnDetails] = useState({});
  const [warehouseDetails, setWarehouseDetails] = useState(null);
  const [warehouseDetailsOrigin, setWarehouseDetailsOrigin] = useState(null);
  const [totalWeight, setTotalWeight] = useState(0); // State for total weight
  const [totalVolume, setTotalVolume] = useState(0); // State for total volume
  const [totalPackages, setTotalPackages] = useState(0); // State for total packages

  // State for Truck Dialog
  const [isTruckDialogOpen, setIsTruckDialogOpen] = useState(false);
  const [trucks, setTrucks] = useState([]);
  const [selectedTruckId, setSelectedTruckId] = useState('');
  const [selectedTruckDetails, setSelectedTruckDetails] = useState(null);
  const [truckLoading, setTruckLoading] = useState(false);
  const [truckError, setTruckError] = useState(null);
  // State for saving truck assignment
  const [isSavingTruckAssignment, setIsSavingTruckAssignment] = useState(false);
  const [saveTruckError, setSaveTruckError] = useState(null); // Specific error for this save

  // All Assignment Data (to check for existing truck and assign to all)
  const [allAssignmentData, setAllAssignmentData] = useState([]);
  // Assigned Truck Details (for display if already assigned)
  const [assignedTruckDetails, setAssignedTruckDetails] = useState(null);
  const [assignedTruckLoading, setAssignedTruckLoading] = useState(false);
  const [assignedTruckError, setAssignedTruckError] = useState(null);

  // State for Error Dialog (General Action Errors)
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState('');
  const [packagesPerAsn, setPackagesPerAsn] = useState({});

  const { hasPermission } = usePermission();
  const { autoT } = useAutoTranslate();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Helper function to fetch details for a list of Assignment Item IDs
  const fetchAssignmentItemDetails = async (itemIds) => {
      if (!itemIds || itemIds.length === 0) {
          console.log("No item IDs provided to fetchAssignmentItemDetails");
          return []; // Return empty if no IDs
      }
      console.log("Fetching details for Assignment Item IDs:", itemIds);
      // Note: Loading/Error state is managed by the calling function (fetchAssignmentItems)

      try {
          const itemDetailPromises = itemIds.map(itemId =>
              axiosInstance.get(`virtual-ramp-assignments/items/${itemId}`)
          );
          const itemDetailResults = await Promise.allSettled(itemDetailPromises);

          const fetchedDetails = [];
          itemDetailResults.forEach(result => {
              if (result.status === 'fulfilled' && result.value.data) {
                  // Adjust data access based on your items/{idItem} response
                  const detailData = result.value.data.data || result.value.data;
                  if (detailData) {
                      fetchedDetails.push(detailData);
                  } else {
                      console.warn("No data found in successful item detail response:", result.value);
                  }
              } else if (result.status === 'rejected') {
                  console.error("Failed to fetch Assignment Item detail:", result.reason);
                  // Append specific item fetch error
                  setAssignmentItemsError(prev => (prev ? prev + '; ' : '') + `Error al cargar detalle de item: ${result.reason.message}`);
              }
          });
          console.log("Fetched Assignment Item Details:", fetchedDetails);
          console.log(rampData)
          return fetchedDetails;

      } catch (err) {
          console.error("Error in fetchAssignmentItemDetails:", err);
          // Set a general error for this phase
          setAssignmentItemsError(prev => (prev ? prev + '; ' : '') + `Error general al cargar detalles de items: ${err.message}`);
          return []; // Return empty on critical error
      }
  };

  // --- Fetch Assigned Truck Details ---
  useEffect(() => {
      const fetchAssignedTruck = async (truckId) => {
          if (!truckId) {
              setAssignedTruckDetails(null);
              setAssignedTruckLoading(false);
              setAssignedTruckError(null);
              return;
          }
          setAssignedTruckLoading(true);
          setAssignedTruckError(null);
          setAssignedTruckDetails(null); // Clear previous
          try {
              console.log("Fetching assigned truck details for ID:", truckId);
              const response = await axiosInstance.get(`trucks/${truckId}`);
              const truckData = response.data.data || response.data;
              if (truckData) {
                  setAssignedTruckDetails(truckData);
              } else {
                  throw new Error("No se encontraron datos para el camión asignado.");
              }
          } catch (err) {
              console.error("Error fetching assigned truck:", err);
              setAssignedTruckError(`Error al cargar detalles del camión asignado: ${err.message}`);
          } finally {
              setAssignedTruckLoading(false);
          }
      };

      // Check if any assignment has a truck assigned (use the first one found)
      const assignmentWithTruck = allAssignmentData.find(assignment => assignment.truckId);
      if (assignmentWithTruck?.truckId) {
          fetchAssignedTruck(assignmentWithTruck.truckId);
      } else {
          // Ensure details are cleared if no truck is assigned
          setAssignedTruckDetails(null);
          setAssignedTruckLoading(false);
          setAssignedTruckError(null);
      }
  }, [allAssignmentData]); // Re-run when assignment data changes

  // --- Fetch Assignment Items (and store all assignments) ---
  const fetchAssignmentItems = async (assignmentIds) => {
      if (!assignmentIds || assignmentIds.length === 0) {
          setAssignmentItemDetails([]);
          setAllAssignmentData([]); // Ensure all assignments are cleared
          return;
      }

      setAssignmentItemsLoading(true);
      setAssignmentItemsError(null);
      setAssignmentItemDetails([]); // Clear previous item details
      setAllAssignmentData([]); // Clear previous assignment data

      let collectedItemIds = [];
      const allAssignments = [];

      try {
          // Fetch all assignments concurrently
          const assignmentPromises = assignmentIds.map(assignId =>
              axiosInstance.get(`virtual-ramp-assignments/${assignId}`)
          );
          const assignmentResults = await Promise.allSettled(assignmentPromises);

          // Process results
          assignmentResults.forEach((result, index) => {
              const currentAssignId = assignmentIds[index];
              if (result.status === 'fulfilled' && result.value.data) {
                  const assignmentData = result.value.data.data || result.value.data;

                  // Store all assignment data
                  allAssignments.push(assignmentData);
                  console.log("Storing assignment data:", assignmentData);

                  // Collect item IDs
                  if (assignmentData && Array.isArray(assignmentData.assignmentItemIds)) {
                      collectedItemIds = collectedItemIds.concat(assignmentData.assignmentItemIds);
                  }
              } else if (result.status === 'rejected') {
                  console.error(`Failed to fetch assignment ${currentAssignId}:`, result.reason);
                  setAssignmentItemsError(prev => (prev ? prev + '; ' : '') + `Error al cargar asignación ${currentAssignId}: ${result.reason.message}`);
              }
          });

          // Set all assignment data
          setAllAssignmentData(allAssignments);

          const uniqueItemIds = [...new Set(collectedItemIds)];
          console.log("Unique Assignment Item IDs to fetch details for:", uniqueItemIds);

          if (uniqueItemIds.length > 0) {
              const itemDetails = await fetchAssignmentItemDetails(uniqueItemIds);
              setAssignmentItemDetails(itemDetails);
          } else {
              setAssignmentItemDetails([]);
              console.log("No unique assignmentItemIds found to fetch details.");
          }
      } catch (err) {
          setAssignmentItemsError(`Error procesando asignaciones y detalles de items: ${err.message}`);
          console.error("Assignment/Item processing error:", err);
      } finally {
          setAssignmentItemsLoading(false);
      }
  };

  // --- Combined fetch for initial ramp data ---
  useEffect(() => {
      const fetchRampData = async () => {
      try {
        setLoading(true);
              setError(null);
              setAssignmentItemsError(null);
              setAssignmentItemDetails([]);
              setAllAssignmentData([]); // Reset on new ramp load
              setAssignedTruckDetails(null); // Reset assigned truck
              setAssignedTruckLoading(false);
              setAssignedTruckError(null);

        const rampResponse = await axiosInstance.get(`virtual-ramps/${id}`);
              const fetchedRampData = rampResponse.data.data || rampResponse.data;

              if (fetchedRampData) {
                  setRampData(fetchedRampData);
                  if (Array.isArray(fetchedRampData.assignmentIds) && fetchedRampData.assignmentIds.length > 0) {
                      await fetchAssignmentItems(fetchedRampData.assignmentIds);
                  } else {
                      setAssignmentItemDetails([]);
                      setAllAssignmentData([]);
                  }

                  // Fetch warehouse details for warehouseOriginId
                  if (fetchedRampData) {
                      console.log("ware",fetchedRampData)
                      fetchWarehouseDetailsOrigin(fetchedRampData.warehouseOriginId);
                  }

                  // Fetch warehouse details for warehouseId (destination)
                  if (fetchedRampData.warehouseId) {
                      fetchWarehouseDetails(fetchedRampData.warehouseId);
                  }
        } else {
          setError('No se pudo cargar la información de la rampla');
        }
          } catch (err) {
              setError(`Error al cargar datos de la rampa: ${err.message}`);
              console.error("Fetch ramp error:", err);
          } finally {
              setLoading(false);
          }
      };

      fetchRampData();
  }, [id]); // Dependency on ramp ID

  // Function to fetch trucks
  const fetchTrucks = async () => {
      if (trucks.length > 0) return; // Don't refetch if already loaded

      setTruckLoading(true);
      setTruckError(null);
      try {
          const response = await axiosInstance.get('trucks'); // Assuming endpoint is /trucks
          // Adjust based on your API structure (e.g., response.data.data, response.data.records.data)
          setTrucks(response.data.records.data || response.data || []);
          console.log(response)
      } catch (err) {
          setTruckError(`Error al cargar camiones: ${err.message}`);
          console.error("Fetch trucks error:", err);
      } finally {
          setTruckLoading(false);
      }
  };

  // Handlers for Truck Dialog
  const handleOpenTruckDialog = () => {
      setIsTruckDialogOpen(true);
      fetchTrucks(); // Fetch trucks when dialog opens
  };

  const handleCloseTruckDialog = (refresh = false) => {
      setIsTruckDialogOpen(false);
      setSelectedTruckId('');
      setSelectedTruckDetails(null);
      setTruckError(null);
      setSaveTruckError(null); // Clear save error on close
      if (refresh) {
          // Refresh page data after successfully assigning a truck
          const fetchRampData = async () => {
              try {
                  setLoading(true);
                  setError(null);
                  setAssignmentItemsError(null);
                  setAssignmentItemDetails([]);
                  setAllAssignmentData([]); // Reset on new ramp load
                  setAssignedTruckDetails(null); // Reset assigned truck
                  setAssignedTruckLoading(false);
                  setAssignedTruckError(null);

                  const rampResponse = await axiosInstance.get(`virtual-ramps/${id}`);
                  const fetchedRampData = rampResponse.data.data || rampResponse.data;
                  if (fetchedRampData) {
                      setRampData(fetchedRampData);
                      if (Array.isArray(fetchedRampData.assignmentIds) && fetchedRampData.assignmentIds.length > 0) {
                          await fetchAssignmentItems(fetchedRampData.assignmentIds); // Re-fetch assignments
                      } else {
                          setAssignmentItemDetails([]);
                          setAllAssignmentData([]);
                      }
                  }
              } catch (err) { setError(`Error recargando datos: ${err.message}`); }
              finally { setLoading(false); }
          };
          fetchRampData(); // Trigger the refresh
      }
  };

  const handleTruckSelectionChange = (event) => {
      const truckId = event.detail.selectedOption.dataset.id; // Get ID from data attribute
      setSelectedTruckId(truckId);
  };

  // Effect to update selected truck details when ID changes
  useEffect(() => {
      if (selectedTruckId) {
          const foundTruck = trucks.find(truck => truck.id === selectedTruckId);
          setSelectedTruckDetails(foundTruck || null);
      } else {
          setSelectedTruckDetails(null);
      }
  }, [selectedTruckId, trucks]);

  const getStatusLabel = (status) => {
    const statusMap = {
      AVAILABLE: autoT('Disponible'),
      OCCUPIED: autoT('Ocupado'),
      FINALIZED: autoT('Finalizado'),
      IN_USE: autoT('En uso'),
      MAINTENANCE: autoT('Mantenimiento'),
      RESERVED: autoT('Reservada')
    };
    return statusMap[status] || status;
  };

  const columns = [
    { 
      Header: 'Número Notificación', 
      accessor: 'asnNumber',
      sortable: true,
      hAlign: 'center'
    },
    { 
      Header: 'Fecha Notificación', 
      accessor: 'asnDate',
      sortable: true,
      disableFilters: true,
      hAlign: 'center',
      Cell: ({ value }) => {
        if (!value) return '';
        const date = new Date(value);
        return date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
    },
    { 
      Header: 'Tipo de envio', 
      accessor: 'shipmentType',
      sortable: true,
      hAlign: 'center'
    },
    { 
      Header: 'Medio de transporte', 
      accessor: 'transportMedium',
      sortable: true,
      hAlign: 'center'
    },
    { 
      Header: 'Tipo de bulto', 
      accessor: 'chargeType',
      sortable: true,
      width: 150,
      hAlign: 'center'
    },
    {
      Header: 'Numero de OC',
      accessor: 'purchaseOrderNumber',
      sortable: true,
      width: 150,
      hAlign: 'center'
    }
  ];

  const formatDate = (dateString, includeTime = true) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
      const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
          ...(includeTime && { hour: '2-digit', minute: '2-digit' }) // Conditionally add time
      };
      return date.toLocaleString('es-ES', options);
  };

  // Helper function for Ramp Type Label
  const getRampTypeLabel = (type) => {
      const typeMap = {
          INBOUND: 'Ingreso',
          OUTBOUND: 'Salida',
          CROSS_DOCK: 'Transbordo',
          // Add other mappings as needed
      };
      return typeMap[type] || type; // Return mapped label or the original value
  };

      const fetchAsnDetails = async (asnDocumentIds) => {
          try {
              const asnDetailPromises = asnDocumentIds.map(asnId =>
                  axiosInstance.get(`/asn-documents/${asnId}`)
              );
              const asnDetailResults = await Promise.allSettled(asnDetailPromises);
              
              const fetchedAsnDetails = {};
              const packagesPerAsn = {}; // almacena paquetes por ASN
              let accumulatedWeight = 0;
              let accumulatedVolume = 0;
              let totalPackages = 0;

              asnDetailResults.forEach((result, index) => {
                  if (result.status === 'fulfilled' && result.value.data) {
                      const asnData = result.value.data.data || result.value.data;
                      const asnId = asnDocumentIds[index];

                      fetchedAsnDetails[asnId] = asnData.asnNumber;
                      const packageCount = Array.isArray(asnData.packages) ? asnData.packages.length : 0;
                      
                      accumulatedWeight += asnData.totalWeight || 0;
                      accumulatedVolume += asnData.totalVolume || 0;
                      totalPackages += packageCount;

                      packagesPerAsn[asnId] = packageCount; // aquí guardamos la cantidad por asn
                  } else {
                      console.error("Failed to fetch ASN detail:", result.reason);
                  }
              });

              setAsnDetails(fetchedAsnDetails);
              setTotalWeight(accumulatedWeight);
              setTotalVolume(accumulatedVolume);
              setTotalPackages(totalPackages);
              setPackagesPerAsn(packagesPerAsn); // finalmente, actualizar el estado completo
          } catch (err) {
              console.error("Error fetching ASN details:", err);
          }
      };


  useEffect(() => {
      const newAsnIds = assignmentItemDetails.map(item => item.asnDocumentId).filter(Boolean);
      if (newAsnIds.length > 0) {
          fetchAsnDetails(newAsnIds);
      }
  }, [assignmentItemDetails]);

  // Define columns for the Assignment Items table
  const assignmentItemColumns = [
      {
          Header: 'N° Notificación',
          accessor: 'asnDocumentId',
          hAlign: 'center',
          Cell: ({ row }) => {
              const asnId = row.original.asnDocumentId;
              if (!asnId) return null;

              return (
                  <Button
                      design="Transparent"
                      onClick={() => navigate(`/asn/${asnId}`)}
                  >
                      {asnDetails[asnId] || "Cargando..."}
                  </Button>
              );
          }
      },
      { 
          Header: 'Peso (kg)', 
          accessor: 'weightTons', 
          hAlign: 'center', 
          Cell: ({ value }) => (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
                  <span style={rightAlignedNumberStyle()}>
                      {formatNumberWithComma(parseFloat(value ?? 0))} kg
                  </span>
              </div>
          )
      },
      { 
          Header: 'Volumen (m³)', 
          accessor: 'volumeCubicMeters', 
          hAlign: 'center', 
          Cell: ({ value }) => (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
                  <span style={rightAlignedNumberStyle()}>
                      {formatNumberWithComma(parseFloat(value ?? 0))} m³
                  </span>
              </div>
          )
      },
      {
          Header: 'N° Bultos',
          accessor: 'asnPackages',
          hAlign: 'center',
          Cell: ({ row }) => {
              const asnId = row.original.asnDocumentId;
              return packagesPerAsn[asnId];
          }
      }
  ];

  // Function to display errors in the main dialog
  const showErrorDialog = (message) => {
      setErrorDialogMessage(message);
      setIsErrorDialogOpen(true);
  };

  // --- Handle Assign Truck ---
  const handleAssignTruck = async () => {
      // Clear previous errors
      setSaveTruckError(null);
      setIsErrorDialogOpen(false);
      setErrorDialogMessage('');

      if (!selectedTruckId) {
          // Use Dialog for validation error
          showErrorDialog("Por favor, seleccione un camión.");
          return;
      }

      if (!allAssignmentData || allAssignmentData.length === 0) {
          const msg = "No se encontraron asignaciones para actualizar.";
          setSaveTruckError(msg); // Show inline in dialog
          showErrorDialog(msg); // Show in main dialog
          console.error("Cannot assign truck: No assignments found");
          return;
      }

      setIsSavingTruckAssignment(true);

      try {
          // Update all assignments with the selected truck
          const assignmentPromises = allAssignmentData.map(assignment => {
              const assignmentPayload = {
                  id: assignment.id,
                  truckId: selectedTruckId,
                  virtualRampId: id,
                  driverName: null,
                  driverPhone: null,
                  driverDocumentNumber: null,
                  scheduledArrivalTime: null,
                  scheduledDepartureTime: null,
                  actualArrivalTime: null,
                  actualDepartureTime: null,
                  status: "FINALIZED",
                  totalAssignedWeightTons: null,
                  totalAssignedVolumeCubicMeters: null,
                  totalAssignedPallets: null,
                  notes: null,
                  cancellationReason: null,
              };

              console.log(`Sending PUT to virtual-ramp-assignments/${assignment.id} with payload:`, assignmentPayload);
              return axiosInstance.put(`virtual-ramp-assignments/${assignment.id}`, assignmentPayload);
          });

          const assignmentResults = await Promise.allSettled(assignmentPromises);
          
          // Check if all assignments were updated successfully
          const failedAssignments = assignmentResults.filter(result => 
              result.status === 'rejected' || 
              (result.status === 'fulfilled' && result.value.status !== 200 && result.value.status !== 204)
          );

          if (failedAssignments.length > 0) {
              const errorMessage = `Error al actualizar ${failedAssignments.length} de ${allAssignmentData.length} asignaciones.`;
              console.error("Error al asignar camión a algunas asignaciones:", failedAssignments);
              setSaveTruckError(errorMessage);
              showErrorDialog(errorMessage);
              return;
          }

          console.log("Camión asignado y estado actualizado a FINALIZED exitosamente en todas las asignaciones.");

          // Now update the virtual ramp status
          const rampPayload = {
              id: id, // The ID of the virtual ramp
              status: "FINALIZED", // Set the status to FINALIZED
              rampCode: null, // Empty fields as specified
              name: null,
              maxWeightCapacityTons: null,
              maxVolumeCapacityCubicMeters: null,
              physicalDockNumber: null,
              isActive: null,
              priority: null,
              rampType: null,
              maxTotalPallet: null,
              warehouseId: null,
              warehouseOriginId: null
          };

          const rampResponse = await axiosInstance.put(`virtual-ramps/${id}`, rampPayload); // Make the PUT request to update the ramp
          if (rampResponse.status === 200 || rampResponse.data?.code === "SUCCESS") {
              console.log(`Estado de la rampa virtual actualizado a FINALIZED.`);
              handleCloseTruckDialog(true); // Close dialog and trigger refresh silently
          } else {
              console.error(`Error al actualizar el estado de la rampa: ${rampResponse.data?.message}`);
              showErrorDialog(`Error al actualizar el estado de la rampa: ${rampResponse.data?.message}`);
          }
      } catch (err) {
          // --- Network/Server Error on Save ---
          console.error("Error al asignar camión:", err);
          const message = err.response?.data?.message || err.message || "Ocurrió un error de red o del servidor.";
          setSaveTruckError(`Error al asignar camión: ${message}`); // Show inline in dialog
          showErrorDialog(`Error al asignar camión: ${message}`); // Show in main dialog
      } finally {
          setIsSavingTruckAssignment(false);
      }
  };
  // -------------------------

  // Determine if a truck is assigned (check if any assignment has a truck)
  const isTruckAssigned = allAssignmentData.some(assignment => assignment.truckId);

  // Function to fetch warehouse details
  const fetchWarehouseDetails = async (warehouseId) => {
      try {
          const response = await axiosInstance.get(`warehouses/${warehouseId}`);
          const warehouseData = response.data.data || response.data;
          if (warehouseData) {
              setWarehouseDetails(warehouseData);
          } else {
              console.warn("No warehouse data found for ID:", warehouseId);
          }
      } catch (err) {
          console.error("Error fetching warehouse details:", err);
      }
  };

  // Function to fetch origin warehouse details
  const fetchWarehouseDetailsOrigin = async (warehouseId) => {
      try {
          const response = await axiosInstance.get(`warehouses/${warehouseId}`);
          console.log("warehouse id", warehouseId, "response", response)
          const warehouseData = response.data.data || response.data;
          if (warehouseData) {
              setWarehouseDetailsOrigin(warehouseData);
          } else {
              console.warn("No warehouse data found for ID:", warehouseId);
          }
      } catch (err) {
          console.error("Error fetching origin warehouse details:", err);
      }
  };

  // Effect to fetch warehouse details when rampData changes
  useEffect(() => {
      if (rampData && rampData.warehouseId) {
          fetchWarehouseDetails(rampData.warehouseId);
      }
  }, [rampData]);

  // --- Render Logic ---
  // Consistent Full-Page Loading Indicator
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <BusyIndicator active size="L" text={autoT("Cargando detalles de la rampla...")} />
      </div>
    );
  }

  // Show initial load error with MessageStrip (Reverted to original simple return)
  if (error && !rampData) {
    return (
      <MessageStrip design="Negative" style={{ margin: '20px' }}>
        {autoT(error)}
      </MessageStrip>
    );
  }

  // Show no data message (Reverted to original simple return)
  if (!rampData && !loading && !error) {
    return (
      <MessageStrip design="Information" style={{ margin: '20px' }}>
              {autoT("No se encontraron datos para esta rampla.")}
      </MessageStrip>
    );
  }

  // --- Main Component Render (when loading is false and data exists) ---
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar toggleSidebar={toggleSidebar} />
      
      <div style={{ display: 'flex', flex: 1, marginTop: '48px' }}>
        <SideMenu isOpen={sidebarOpen} />
        
        <div style={{ 
          flex: 1,
          marginLeft: sidebarOpen ? '250px' : '0',
          transition: 'margin-left 0.3s',
          padding: '20px',
          backgroundColor: '#f5f6f7'
        }}>
          
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            marginTop: '10px'
          }}>
            <Title level="H2">{autoT("Rampla")}: {rampData.rampCode}</Title>
            <div style={{ 
            display: 'flex', 
            gap: '1rem'
          }}>
          {hasPermission('virtualramp', 'update') && (
            <>
              <Button
                icon="shipping-status"
                onClick={handleOpenTruckDialog}
                disabled={
                  assignmentItemsLoading || 
                  assignmentItemDetails.length === 0 ||
                  isTruckAssigned
                }
              >
                {autoT("Equipo")}
              </Button>
              <Button
                icon="workflow-tasks"
                onClick={() => navigate(`/create-assignment/${rampData.id}`)}
                disabled={isTruckAssigned}
              >
                {autoT("Asignar")}
              </Button>
            </>
          )}
            </div>
          </div>

          {/* Sección de Información General */}
          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            marginBottom: '20px',
            borderRadius: '8px',
            boxShadow: '0 0 4px rgba(0,0,0,0.1)',
            display: 'flex', // Use flexbox for layout
            justifyContent: 'space-between', // Space between sections
            alignItems: 'flex-start' // Align items to the start
          }}>
                      <div style={{ flex: '1 1 300px' }}>
            <Title level="H3" style={{ marginBottom: '15px' }}>{autoT("Información General")}</Title>
            
                <Label>{autoT("Nombre")}:</Label>
                <Text style={{ display: 'block', margin: '5px 0 15px 0' }}>
                  {rampData.name}
                </Text>
                
                <Label>{autoT("Código")}:</Label>
                <Text style={{ display: 'block', margin: '5px 0 15px 0' }}>
                  {rampData.rampCode}
                </Text>
                
                <Label>{autoT("Muelle Físico")}:</Label>
                <Text style={{ display: 'block', margin: '5px 0 15px 0' }}>
                  {rampData.physicalDockNumber}
                </Text>


                          {/* New fields for warehouse details */}
                          {warehouseDetails && (
                              <>
                              <Label>{autoT("Bodega Destino")}:</Label>
                                  <Text style={{ display: 'block', margin: '5px 0 15px 0' }}>
                                      {warehouseDetails.name}
                                  </Text>
                                  <Label>{autoT("Tipo de bodega Destino")}:</Label>
                                  <Text style={{ display: 'block', margin: '5px 0 15px 0' }}>
                                      {warehouseDetails.warehouseType}
                                  </Text>
                              </>
                          )}

                          {warehouseDetailsOrigin && (
                              <>
                                  <Label>{autoT("Bodega Origen")}:</Label>
                                  <Text style={{ display: 'block', margin: '5px 0 15px 0' }}>
                                      {warehouseDetailsOrigin.name}
                                  </Text>
                                  <Label>{autoT("Tipo de bodega Origen")}:</Label>
                                  <Text style={{ display: 'block', margin: '5px 0 15px 0' }}>
                                      {warehouseDetailsOrigin.warehouseType}
                                  </Text>
                              </>
                          )}
              </div>
              
                      <div style={{ flex: '1 1 300px', marginLeft: '20px' }}> {/* Add margin for spacing */}
                      <div style={{ display: 'flex', alignItems: 'center', margin: '5px 0 15px 0' }}>
              <Label>{autoT("Estado Rampla")}:</Label>
              <ObjectStatus
                style={getStatusComponentProps(rampData.status, null, { 
                  marginLeft: '10px',
                  width: '120px'
                }).style}
              >
                {getStatusLabel(rampData.status)}
              </ObjectStatus>
            </div>
                      
                          <Label>{autoT("Tipo de rampla")}:</Label>
                          <Text style={{ display: 'block', margin: '5px 0 15px 0' }}>
                              {getRampTypeLabel(rampData.rampType)}
                          </Text>
                
                <Label>{autoT("Activa")}:</Label>
                <div style={{ margin: '5px 0 15px 0' }}>
                  {rampData.isActive ? (
                    <Icon name="accept" style={{ color: '#28a745' }} />
                  ) : (
                    <Icon name="decline" style={{ color: '#dc3545' }} />
                  )}
                </div>
                
                </div>
                      {isTruckAssigned && assignedTruckDetails && !assignedTruckLoading && (
                          <div style={{
                              backgroundColor: 'white',
                              padding: '20px',
                              boxShadow: '0 0 4px rgba(0,0,0,0.1)',
                              flex: '1 1 300px', // Ensure it takes space
                              marginLeft: '20px' // Add margin for spacing
                          }}>
                              <Title level="H3" style={{ marginBottom: '15px' }}>{autoT("Equipo Asignado")}</Title>
                              <FlexBox direction="Column" style={{ gap: '0.5rem' }}>
                                  <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Patente")}:</Label><Text>{assignedTruckDetails.licensePlate}</Text></FlexBox>
                                  <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Marca")}:</Label><Text>{assignedTruckDetails.brand}</Text></FlexBox>
                                  <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Modelo")}:</Label><Text>{assignedTruckDetails.model}</Text></FlexBox>
                                  <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Año")}:</Label><Text>{assignedTruckDetails.year}</Text></FlexBox>
                                  <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Capacidad (kg)")}:</Label><Text>{formatNumberWithComma(assignedTruckDetails.capacityTons)}</Text></FlexBox>
                                  <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Capacidad (m³)")}:</Label><Text>{formatNumberWithComma(assignedTruckDetails.capacityCubicMeters)}</Text></FlexBox>
                                  <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Activo")}:</Label><Text>{assignedTruckDetails.isActive ? 'Sí' : 'No'}</Text></FlexBox>
                                  <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Venc. Seguro")}:</Label><Text>{formatDate(assignedTruckDetails.insuranceExpiryDate, false)}</Text></FlexBox>
                                  <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Venc. Rev. Técnica")}:</Label><Text>{formatDate(assignedTruckDetails.technicalReviewExpiryDate, false)}</Text></FlexBox>
                              </FlexBox>
              </div>
                      )}
          </div>

          {/* Sección de Capacidades */}
          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            marginBottom: '20px',
            borderRadius: '8px',
            boxShadow: '0 0 4px rgba(0,0,0,0.1)'
          }}>
            <Title level="H3" style={{ marginBottom: '15px' }}>{autoT("Cantidades")}</Title>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '30px' }}>
              <div style={{ flex: '1 1 300px' }}>
                <Label>{autoT("Peso")}:</Label>
                <Text style={{ display: 'block', margin: '5px 0 15px 0' }}>
                  {formatNumberWithComma(totalWeight)} kg / {formatNumberWithComma(rampData.maxWeightCapacityTons)} kg
                </Text>
              </div>
              
              <div style={{ flex: '1 1 300px' }}>
                <Label>{autoT("Volumen")}:</Label>
                <Text style={{ display: 'block', margin: '5px 0 15px 0' }}>
                  {formatNumberWithComma(totalVolume)} m³ / {formatNumberWithComma(rampData.maxVolumeCapacityCubicMeters)} m³
                </Text>
              </div>

              <div style={{ flex: '1 1 300px' }}>
                <Label>{autoT("Total de bultos")}:</Label>
                <Text style={{ display: 'block', margin: '5px 0 15px 0' }}>
                  {totalPackages}
                </Text>
              </div>
            </div>
          </div>
          {/* Sección de Asignaciones */}
          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 0 4px rgba(0,0,0,0.1)'
          }}>
                      <Title level="H3" style={{ marginBottom: '15px' }}>{autoT("Notificaciones Asignadas")}</Title>

                      {assignmentItemsLoading && <BusyIndicator active size="Medium" text={autoT("Cargando notificaciones asignados...")} />}
                      {assignmentItemsError && <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>{autoT(assignmentItemsError)}</MessageStrip>}

                      {!assignmentItemsLoading && !assignmentItemsError && assignmentItemDetails.length > 0 && (
            <AnalyticalTable
                              data={assignmentItemDetails}
                              columns={assignmentItemColumns}
                              minRows={5}
              style={{
                width: '100%',
                                  maxHeight: '550px',
                                  overflowY: 'auto',
                '--sapList_HeaderBackground': '#f5f6f7',
                '--sapList_BorderColor': '#e5e5e5',
              }}
              header
              alternateRowColor
                              sortable
                          />
                      )}
                      { !assignmentItemsLoading && !assignmentItemsError && assignmentItemDetails.length === 0 && (
                          <Text style={{ marginTop: '10px', fontStyle: 'italic' }}>{autoT("No tiene notificaciones asignados.")}</Text>
                      )}
                  </div>

                  {/* --- Assigned Truck Section --- */}
                  {assignedTruckLoading && (
                      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 0 4px rgba(0,0,0,0.1)', marginTop: '20px' }}>
                          <BusyIndicator active size="Medium" text="Cargando detalles del camión asignado..." />
                      </div>
                  )}
                  {assignedTruckError && (
                      <div style={{ marginTop: '20px' }}>
                          <MessageStrip design="Negative">{assignedTruckError}</MessageStrip>
                      </div>
                  )}

              </div>
          </div>

          {/* Truck Selection Dialog - Updated Footer */}
          <Dialog
              open={isTruckDialogOpen}
              headerText={autoT("Seleccionar Camión")}
              onAfterClose={() => handleCloseTruckDialog(false)} // Pass false on normal close
              footer={
                  <FlexBox justifyContent="End" style={{ width: '100%', paddingRight: '1rem', paddingBottom: '0.5rem', gap: '0.5rem' }}>
                      {/* Select Truck Button */}
                      <Button
                          design="Emphasized"
                          onClick={handleAssignTruck}
                          disabled={!selectedTruckId || isSavingTruckAssignment} // Disable if no truck selected or saving
                      >
                          {isSavingTruckAssignment ? autoT("Guardando...") : autoT("Seleccionar")}
                      </Button>
                      <Button onClick={() => handleCloseTruckDialog(false)}>{autoT("Cerrar")}</Button>
                  </FlexBox>
              }
              style={{ minWidth: '400px', maxWidth: '600px' }}
          >
              <div style={{ padding: '20px' }}>
                  {truckLoading && <BusyIndicator active size="Medium" />}
                  {/* Show truck fetch error OR truck save error */}
                  {(truckError || saveTruckError) && (
                      <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>
                          {saveTruckError || truckError}
                      </MessageStrip>
                  )}

                  {!truckLoading && ( // Render select only when not loading trucks
                      <>
                          <Label required style={{ display: 'block', marginBottom: '5px' }}>{autoT("Equipo")}</Label>
                          <Select
                              style={{ width: '100%', marginBottom: '20px' }}
                              onChange={handleTruckSelectionChange}
                              disabled={isSavingTruckAssignment} // Disable select while saving
                          >
                              <Option value="">{autoT("-- Seleccionar Equipo --")}</Option>
                              {trucks.map((truck) => (
                                  <Option key={truck.id} value={truck.licensePlate} data-id={truck.id} selected={selectedTruckId === truck.id}>
                                      {truck.licensePlate} ({truck.brand} {truck.model})
                                  </Option>
                              ))}
                          </Select>

                          {/* Display Selected Truck Details */}
                          {selectedTruckDetails && (
                              <div>
                                  <Title level="H4" style={{ marginTop: '20px', marginBottom: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>{autoT("Detalles del Equipo Seleccionado")}</Title>
                                  <FlexBox direction="Column" style={{ gap: '0.5rem' }}>
                                      <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Patente")}:</Label><Text>{selectedTruckDetails.licensePlate}</Text></FlexBox>
                                      <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Marca")}:</Label><Text>{selectedTruckDetails.brand}</Text></FlexBox>
                                      <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Modelo")}:</Label><Text>{selectedTruckDetails.model}</Text></FlexBox>
                                      <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Año")}:</Label><Text>{selectedTruckDetails.year}</Text></FlexBox>
                                      <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Capacidad (Ton)")}:</Label><Text>{selectedTruckDetails.capacityTons}</Text></FlexBox>
                                      <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Capacidad (m³)")}:</Label><Text>{selectedTruckDetails.capacityCubicMeters}</Text></FlexBox>
                                      <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Activo")}:</Label><Text>{selectedTruckDetails.isActive ? 'Sí' : 'No'}</Text></FlexBox>
                                      <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Venc. Seguro")}:</Label><Text>{formatDate(selectedTruckDetails.insuranceExpiryDate)}</Text></FlexBox>
                                      <FlexBox style={{ gap: '1rem' }}><Label style={{ fontWeight: 'bold', width: '150px' }}>{autoT("Venc. Rev. Técnica")}:</Label><Text>{formatDate(selectedTruckDetails.technicalReviewExpiryDate)}</Text></FlexBox>
                                  </FlexBox>
                              </div>
                          )}
                      </>
                  )}
              </div>
          </Dialog>

          {/* --- General Error Dialog --- */}
          <Dialog
              open={isErrorDialogOpen}
              headerText={autoT("Error")}
              onAfterClose={() => setIsErrorDialogOpen(false)}
              footer={
                  <FlexBox justifyContent={FlexBoxJustifyContent.End} style={{ width: '100%', paddingRight: '1rem', paddingBottom: '0.5rem' }}>
                      <Button onClick={() => setIsErrorDialogOpen(false)}>{autoT("Cerrar")}</Button>
                  </FlexBox>
              }
          >
              <div style={{ padding: '20px' }}>
                  <Text>{errorDialogMessage}</Text>
        </div>
          </Dialog>
          {/* --- End General Error Dialog --- */}

    </div>
  );
};

export default RampDetail;